// client/src/components/people/PersonModal.js
// The one detail modal for a person, opened from the Individuals and RGA
// Partners tables. RGA Partners also get the agreement/licensed badges and
// Active Mandates. "Edit" swaps the body for PersonForm.
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/api'
import { isRgaPartner } from '../../lib/roles'
import { firmsOf } from '../../lib/contactFirms'
import EventsAttended from '../EventsAttended'
import ActivityTimeline from '../ActivityTimeline'
import PersonForm from './PersonForm'
import { CONTACT_TYPE_BADGE, invalidatePeople, Modal, Field, formatDate, tagColor } from './ui'

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>
      {children}
    </div>
  )
}

export default function PersonModal({ person, profileBase, onClose, firms, allContacts, users, tagSuggestions }) {
  const [editMode, setEditMode] = useState(false)
  const qc = useQueryClient()
  const id = person.contact_id
  const partner = isRgaPartner(person)

  // This person's interactions (shared cache with ActivityTimeline), for Associated Mandates
  const { data: activityData } = useQuery({
    queryKey: ['interactions', { contact_id: id }],
    queryFn: () => api.get('/api/interactions', { params: { contact_id: id } }).then(r => r.data.data ?? []),
  })
  const linkedMandates = useMemo(() => {
    const seen = new Set()
    return (activityData ?? [])
      .filter(i => i.mandate && !seen.has(i.mandate.mandate_id) && seen.add(i.mandate.mandate_id))
      .map(i => i.mandate)
  }, [activityData])

  // Active mandates come with /api/partners rows; opened from Individuals,
  // look the partner up in that (shared) list instead.
  const { data: partnersData } = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get('/api/partners').then(r => r.data.data ?? []),
    enabled: partner && !person.active_mandates,
  })
  const activeMandates = person.active_mandates
    ?? partnersData?.find(p => p.contact_id === id)?.active_mandates ?? []

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/contacts/${id}`, payload).then(r => r.data),
    onSuccess: () => {
      invalidatePeople(qc, id)
      setEditMode(false)
    },
  })

  const ownerNames = [].concat(person.relationship_owner ?? []).map(oid => {
    const u = (users ?? []).find(u => u.id === oid)
    return u ? u.name : oid.slice(0, 8) + '...'
  })
  const introducer = person.introduced_by
    ? (allContacts ?? []).find(c => c.contact_id === person.introduced_by)?.name ?? person.introduced_by.slice(0, 8) + '...'
    : null

  if (editMode) {
    return (
      <Modal title={`Edit - ${person.name}`} onClose={onClose} width={600}>
        <PersonForm
          initial={person}
          firms={firms}
          allContacts={(allContacts ?? []).filter(c => c.contact_id !== id)}
          users={users}
          tagSuggestions={tagSuggestions}
          onSubmit={updateMutation.mutateAsync}
          onCancel={() => setEditMode(false)}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.response?.data?.error}
        />
      </Modal>
    )
  }

  const dsc = person.days_since_contact

  return (
    <Modal onClose={onClose} width={600}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{person.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
              {person.title_role && <div>{person.title_role}</div>}
              {firmsOf(person).map(f => <div key={f.firm_id}>{f.name}</div>)}
            </div>
            {(person.tags ?? []).length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
                {person.tags.map(t => (
                  <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 11 }}>{t}</span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <Link to={`${profileBase}/${id}`} className="btn btn-secondary btn-sm">Full profile</Link>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>Edit</button>
            <button className="btn-icon" onClick={onClose} title="Close" style={{ fontSize: 16, marginLeft: 4 }}>✕</button>
          </div>
        </div>

        {/* Types, plus partner status */}
        {((person.contact_type ?? []).length > 0 || partner) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(person.contact_type ?? []).map(t => (
              <span key={t} className={`badge ${CONTACT_TYPE_BADGE[t] ?? 'badge-gray'}`}>{t}</span>
            ))}
            {partner && <>
              <span className={`badge ${person.partnership_agreement ? 'badge-green' : 'badge-gray'}`}>
                {person.partnership_agreement ? '✓ Partnership Agreement' : '✗ No Agreement'}
              </span>
              <span className={`badge ${person.is_licensed ? 'badge-green' : 'badge-gray'}`}>
                {person.is_licensed ? '✓ Licensed' : '✗ Not Licensed'}
              </span>
            </>}
          </div>
        )}

        <div className="divider" />

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Email">
            {(person.email ?? []).map((e, i) => (
              <div key={i}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>
            ))}
          </Field>
          <Field label="Phone">
            {(person.phone ?? []).join(', ') || null}
          </Field>
          <Field label="Relationship Owner">{ownerNames.map(n => <div key={n}>{n}</div>)}</Field>
          <Field label="Introduced By">{introducer}</Field>
          <Field label="Last Interaction">{formatDate(person.last_interaction)}</Field>
          <Field label="Days Since Contact">
            {dsc != null
              ? <span style={{ color: dsc > 30 ? 'var(--danger)' : 'var(--text-primary)' }}>{dsc}d</span>
              : null}
          </Field>
        </div>

        {/* Notes */}
        {person.notes && (
          <>
            <div className="divider" />
            <div>
              <SectionLabel>Notes</SectionLabel>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                {person.notes}
              </div>
            </div>
          </>
        )}

        {/* Active Mandates (RGA Partners) */}
        {partner && activeMandates.length > 0 && (
          <>
            <div className="divider" />
            <div>
              <SectionLabel>Active Mandates</SectionLabel>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {activeMandates.map(m => (
                  <span key={m.mandate_id} className="badge badge-blue">{m.mandate_name}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Associated mandates, from interactions */}
        {linkedMandates.length > 0 && (
          <>
            <div className="divider" />
            <div>
              <SectionLabel>Associated Mandates</SectionLabel>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {linkedMandates.map(m => (
                  <span key={m.mandate_id} className="badge badge-blue">{m.mandate_name}</span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Events attended (conferences etc.) */}
        <div className="divider" />
        <EventsAttended contactId={id} />

        {/* Activity timeline */}
        <div className="divider" />
        <ActivityTimeline contactId={id} emails={person.email ?? []} />

      </div>
    </Modal>
  )
}
